import getSocket from '@/globals/socketConfig';
import { Portal } from '@radix-ui/react-tooltip';
import parse, { domToReact } from 'html-react-parser';
import Link from 'next/link';
import { toast } from 'sonner';

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { TypeAnimation } from 'react-type-animation';

import anticipateDetail from '@/utils/anticipateRender/detail';
import { markdownToComponent, markdownToHtml } from '@/utils/convertMarkdown';
import formatName from '@/utils/format/formatName';
import getCurrentIds from '@/utils/getCurrentIds';
import getIcon from '@/utils/getIcon';
import useTraceUpdate from '@/utils/hooks/useTraceUpdate';
import { cn } from '@/utils/lib/utils';
import { X } from '@/utils/lucide';
import { RotateCw } from '@/utils/lucide';
import { Check } from '@/utils/lucide';
import { ChevronDown } from '@/utils/lucide';
import { ChevronUp } from '@/utils/lucide';
import { Image } from '@/utils/lucide';
import { ExternalLink } from '@/utils/lucide';
import { List } from '@/utils/lucide';
import newTab from '@/utils/mutateSession/newTab';
import sendChatMessage from '@/utils/mutateSession/sendChatMessage';
import openDetail from '@/utils/openDetail';
import { scrollToElement } from '@/utils/scrollToElement';
import showToast from '@/utils/toast/showToast';

import { setOverlay } from '@/redux/layoutSlice';
import store from '@/redux/store';
import { setActiveTabIndex } from '@/redux/tabSlice';

import Message, { MessageSource } from '@/types/Message';
import RoleInfo from '@/types/RoleInfo';

import anticipateRecordSearchDetail from './anticipateRender/recordSearch';

/**
 * Simplified source type covering the essential fields needed for source handling
 */
interface UnifiedSource
{
	// Primary identifiers
	id?: string;
	document_id?: string;

	// Type classification
	doc_type?: string;
	action_type?: string;
	source_type?: string;

	// URL for web sources
	url?: string;

	// Legacy fields for backward compatibility
	source_id?: string;
	status_id?: string;
	entity_id?: string;
	type?: string;
	status_type?: string;

	// May include filters for a search
	filters?: {
		data_filters: any[];
		entity_type: string;
	};

	// unsure if this is needed lets try without it
	// [key: string]: any;
}

/**
 * Unified source handler that supports all source formats:
 * - Supporting quotes (SupportingQuote)
 * - Source items within research log (SourceItem)
 * - Source list items (Document/Web/Tab sources)
 * - In-line citations
 */
const getSource = (source: UnifiedSource) =>
{
	if (source.action_type === 'profile_view')
	{
		handleProfileView(source);
		return;
	}
	if (source.filters)
	{
		handleSearchSource(source);
		return;
	}
	if (source.doc_type === 'tab')
	{
		handleTabSource(source);
		return;
	}
	if (source.doc_type === 'webpage' || source.source_type === 'webpage')
	{
		handleWebpageSource(source);
		return;
	}
	if (source.document_id || source.id)
	{
		handleDocumentSource(source);
		return;
	}

	console.warn('Unknown source format:', source);
	showToast({
		variant: 'outline',
		title: 'Unknown Source Format',
		description: 'This source format is not supported.'
	});
};

const handleProfileView = (source: UnifiedSource) =>
{
	console.log('Opening profile view for:', source.id);
	const id = source.id ?? source.entity_id;

	anticipateDetail();
	socket.emit('take_action', {
		action_type: `${source.source_type}_detail`,
		id,
		session_id: getCurrentIds().session_id
	});
};

/**
 * Handle tab sources by switching to the existing tab
 */
const handleTabSource = (source: UnifiedSource) =>
{
	console.log('Opening tab with ID: ', source.id);
	const tabIndex = store.getState().session.sessionData?.tabs?.findIndex(tab => tab.id === source.id);
	const activeTabIndex = store.getState().tab.activeTabIndex;

	// If the tab is already open, notify the user
	if (tabIndex === activeTabIndex)
	{
		console.log('Tab is already open');
		showToast({
			variant: 'outline',
			title: 'Tab Already Open',
			description: 'This tab is already open.'
		});
		return;
	}

	// Switch to the existing tab
	if (tabIndex !== undefined && tabIndex > -1)
	{
		console.log('Setting active tab index:', tabIndex);
		store.dispatch(setActiveTabIndex(tabIndex));
	}
	else
	{
		console.error('Could not find tab index:', source.id);
	}
};

/**
 * Handle search sources by opening a new search
 */
const handleSearchSource = (source: UnifiedSource) =>
{
	console.log('Opening search with filters:', source.filters);
	const { session_id } = getCurrentIds();

	anticipateRecordSearchDetail({});

	socket.emit('take_action', {
		action_type: 'search',
		search_type: source.source_type ?? source.filters!.entity_type,
		session_id,
		tab_id: null,
		new_search: true,
		filters: {
			query: '',
			page: 1,
			per_page: 25,
			order_by: [],
			order_direction: [],
			data_filters: [],
			...source.filters
		}
	});
};

/**
 * Handle webpage sources by opening URL in new browser tab
 */
const handleWebpageSource = (source: UnifiedSource) =>
{
	const url = source.url;
	if (url)
	{
		window.open(url, '_blank');
	}
	else
	{
		console.error('Webpage source missing URL:', source);
	}
};

/**
 * Handle document sources by emitting document_detail request
 */
const handleDocumentSource = (source: UnifiedSource) =>
{
	anticipateDetail();

	const { tab_id, session_id } = getCurrentIds();
	const documentId = source.document_id || source.id;

	console.log('Opening document source with ID: ', documentId);

	// Emit document_detail action for all document types
	socket.emit('take_action', {
		action_type: 'document_detail',
		id: documentId,
		source_id: source.id, // Include source_id if available for context
		session_id,
		tab_id,
		message_source: true
	});
};

/**
 * Legacy event handler - now unified with main getSource logic
 * Handles entity types like trial, drug, company with special detail actions
 */
const getSourceEventHandler = (e: any, item: UnifiedSource) =>
{
	const type = item.type ?? item.source_type ?? item.status_type;
	const id = item.id ?? item.source_id ?? item.status_id ?? item.entity_id;

	// Handle special entity types that need specific detail actions
	if (type === 'trial' || type === 'drug' || type === 'company')
	{
		const { session_id } = getCurrentIds();
		socket.emit('take_action', {
			action_type: `${type}_detail`,
			[`${type}_id`]: id,
			id,
			session_id
		});
		anticipateDetail();
		return;
	}

	// Handle field type (no action needed)
	if (type === 'field')
	{
		return;
	}

	// For all other types, use the unified source handler
	const sourceObject: UnifiedSource = {
		...item,
		doc_type: type,
		source_type: type,
		id: id
	};

	getSource(sourceObject);
};

export default getSource;
export { getSourceEventHandler };
export type { UnifiedSource };
